import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import RouteArticleCta from "../components/RouteArticleCta";

export default function JournalPostPage() {
    const { slug } = useParams();
    const [post, setPost] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        api.get(`/blog/posts/${slug}`)
            .then((r) => setPost(r.data))
            .catch((e) =>
                setError(e?.response?.status === 404 ? "Article not found." : "Could not load article.")
            );
    }, [slug]);

    if (error) {
        return (
            <div className="bg-brand-paper min-h-[60vh] flex items-center justify-center px-6">
                <div className="text-center">
                    <h1 className="font-serif text-3xl text-brand-ink mb-4">{error}</h1>
                    <Link to="/journal" className="text-brand-ink underline">
                        Back to Journal
                    </Link>
                </div>
            </div>
        );
    }

    if (!post) {
        return (
            <div className="bg-brand-paper min-h-[60vh] flex items-center justify-center text-brand-muted">
                Loading…
            </div>
        );
    }

    return (
        <article className="bg-brand-paper" data-testid="journal-post-page">
            {post.cover_image && (
                <div className="image-zoom h-[420px] lg:h-[560px] overflow-hidden">
                    <img
                        src={post.cover_image}
                        alt={post.title}
                        className="w-full h-full object-cover"
                    />
                </div>
            )}
            <section className="mx-auto max-w-3xl px-6 lg:px-10 py-16 lg:py-24">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-gold font-semibold mb-5">
                        — {new Date(post.created_at).toLocaleDateString("en-CA", {
                            year: "numeric",
                            month: "long",
                            day: "numeric",
                        })}
                    </div>
                    <h1 className="font-serif text-4xl lg:text-6xl leading-[1.1] tracking-tight text-brand-ink">
                        {post.title}
                    </h1>
                    <div className="mt-5 text-sm text-brand-muted">By {post.author}</div>
                    <div className="editorial-rule my-10" />
                    <div className="journal-prose max-w-none text-brand-ink">
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            components={{
                                h1: (p) => <h1 className="font-serif text-3xl lg:text-4xl text-brand-ink mt-12 mb-5 leading-tight" {...p} />,
                                h2: (p) => <h2 className="font-serif text-2xl lg:text-3xl text-brand-ink mt-12 mb-4 leading-tight" {...p} />,
                                h3: (p) => <h3 className="font-serif text-xl lg:text-2xl text-brand-ink mt-10 mb-3 leading-tight" {...p} />,
                                p: (p) => <p className="text-lg text-brand-ink/85 leading-relaxed mb-5" {...p} />,
                                ul: (p) => <ul className="list-disc pl-6 space-y-2 mb-5 text-brand-ink/85" {...p} />,
                                ol: (p) => <ol className="list-decimal pl-6 space-y-2 mb-5 text-brand-ink/85" {...p} />,
                                li: (p) => <li className="leading-relaxed" {...p} />,
                                strong: (p) => <strong className="text-brand-ink font-semibold" {...p} />,
                                em: (p) => <em className="italic" {...p} />,
                                a: ({ href, ...rest }) => (
                                    <a
                                        href={href}
                                        className="text-brand-ink underline underline-offset-4 hover:text-black"
                                        target={href && href.startsWith("http") ? "_blank" : undefined}
                                        rel="noreferrer"
                                        {...rest}
                                    />
                                ),
                                table: (p) => (
                                    <div className="overflow-x-auto my-8">
                                        <table className="w-full border-collapse text-sm" {...p} />
                                    </div>
                                ),
                                thead: (p) => <thead className="bg-brand-sand" {...p} />,
                                th: (p) => <th className="text-left p-3 border border-brand-line font-semibold text-brand-ink" {...p} />,
                                td: (p) => <td className="p-3 border border-brand-line text-brand-ink/85 align-top" {...p} />,
                                blockquote: (p) => <blockquote className="border-l-2 border-brand-gold pl-5 italic text-brand-muted my-6" {...p} />,
                                hr: () => <hr className="my-10 border-brand-line" />,
                            }}
                        >
                            {post.content}
                        </ReactMarkdown>
                    </div>
                    <RouteArticleCta slug={slug} />
                    <div className="mt-16 flex flex-wrap gap-4 items-center justify-between border-t border-brand-line pt-10">
                        <Link
                            to="/journal"
                            className="text-sm tracking-wide text-brand-ink hover:text-black inline-flex items-center gap-2"
                            data-testid="journal-back-link"
                        >
                            <span aria-hidden>←</span> Back to Journal
                        </Link>
                        <Link
                            to="/get-started"
                            data-testid="journal-cta-button"
                            className="inline-flex items-center gap-3 bg-brand-ink text-brand-paper px-6 py-3 text-sm tracking-wide hover:bg-black transition-all hover:gap-4"
                        >
                            Get Matched Now! <span aria-hidden>→</span>
                        </Link>
                    </div>
                </motion.div>
            </section>
        </article>
    );
}
